import cx from 'classnames'
import { Link } from 'features/i18n/router'
import take from 'lodash/take'
import * as queryString from 'query-string'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import { useModalState } from 'hooks'
import { getIsEternalImageMode, setEternalImageModeTo } from 'store/actions'
import { useGetChatRoomQuery } from 'store/api/yay/chat'
import { useCurrentUser } from 'store/api/yay/user'
import { MODAL_MODE } from './config'
import {
  toggleBlockUserConfirmBox,
  toggleUnFollowConfirmBox,
} from 'store/actions/confirmBox'

import useChatRoomLogic from 'features/Chat/hocs/useChatRoomLogic'
import usePageModal from 'features/Modal/hooks/usePageModal'
import { ROUTE_PATH } from 'lib/paths'
import { ChatRoom, User } from 'lib/_types'
import {
  chatRoomToChatRoomContextSchema,
  logEvent,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'

import Avatar from '../Avatar'
import Button from '../Button'
import Nickname from '../Nickname'
import Modal from './Modal'
import { Switch } from '../Form/Switch'
import { UserList } from '../UserList'
import { useReportModal } from './ReportModal/ReportModal'

const HeaderSection = ({ isGroupChat, members }) => {
  const memberListModal = useModalState()
  const { t } = useTranslation()

  return (
    <div className="ChatSettings__header">
      {isGroupChat ? (
        <Fragment>
          <div className="ChatSettings__header__images">
            {take(members, 3).map((member: any) => (
              <Avatar key={member?.id} user={member} />
            ))}
          </div>

          <h3 className="ChatSettings__header__h3">
            ({members.length}){' '}
            {members.map((member: any) => member?.nickname).join(', ')}
          </h3>

          <a
            className="ChatSettings__header__a"
            onClick={() => memberListModal.open()}
          >
            {t('chat_room_detail_modal.header_section.title')}
          </a>

          <Modal
            onClose={memberListModal.close}
            isActive={memberListModal.isActive}
            title={t('chat_room_detail_modal.header_section.member_list.title')}
          >
            <UserList users={members} />
          </Modal>
        </Fragment>
      ) : (
        <Fragment>
          <Link
            to={ROUTE_PATH.USER(members?.[0]?.id)}
            onClick={() =>
              logEvent.user_profile_open({
                target_user_id: members?.[0]?.id,
                from: 'undefined',
              })
            }
          >
            <Avatar user={members?.[0] ?? {}} showVipCircle />
          </Link>

          <h3 className="ChatSettings__header__h3">
            <Nickname user={members?.[0] ?? {}} />
          </h3>
        </Fragment>
      )}
    </div>
  )
}

const BodySection = ({
  isGroupChat,
  chatRoom,
  members,
  showReportUsersModal,
}: {
  isGroupChat?: boolean
  chatRoom?: ChatRoom
  members: User[]
  showReportUsersModal: () => void
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const reportModal = useReportModal()
  const { data: currentUser } = useCurrentUser()

  const { generatePageModalUrl, open } = usePageModal()
  const { onDeleteChat } = useChatRoomLogic({ chatRoomId: chatRoom?.id })

  const isEternalImageMode = useSelector((state) =>
    getIsEternalImageMode(state)(chatRoom?.id)
  )

  const isChatOwner = chatRoom?.owner?.id == currentUser?.id

  const getSettingList = () => {
    const setting = []

    if (isGroupChat) {
      setting.push({
        label: t('common.report'),
        onClick: showReportUsersModal,
      })
    } else {
      const opponent = members?.[0]

      setting.push({
        label: t('common.block'),
        onClick: () =>
          dispatch(
            toggleBlockUserConfirmBox({
              user: opponent,
              returnPath: generatePageModalUrl(MODAL_MODE.CHAT_LIST),
            })
          ),
      })

      setting.push({
        label: t('common.report'),
        onClick: () => {
          if (opponent) {
            reportModal.open({
              chatRoomId: chatRoom?.id?.toString(),
              userId: opponent.id?.toString(),
            })
          }
        },
      })

      if (opponent?.following) {
        setting.push({
          label: t('common.unfollow'),
          onClick: () =>
            dispatch(
              toggleUnFollowConfirmBox({
                user: opponent,
                confirmBoxProps: {
                  onOk: () => open(MODAL_MODE.CHAT_LIST),
                },
              })
            ),
        })
      }
    }

    return [
      {
        label: isChatOwner
          ? t('common.delete')
          : t('chat_room_detail_modal.body_section.setting.leave'),
        className: 'ChatSettings__body__ul__a--red',
        onClick: () => {
          onDeleteChat({
            onSuccess: () => {
              snowplowEvent.delete_chat({
                user: userToUserContextSchemaData({ user: members?.[0] }),
                chatRoom: chatRoomToChatRoomContextSchema({ chatRoom }),
              })

              open(MODAL_MODE.CHAT_LIST)
            },
            isChatOwner,
          })
        },
      },
      ...setting,
      {
        element: (
          <div className="ChatSettings__body__label">
            <div className="ChatSettings__body__label__wrapper">
              <span className="ChatSettings__body__ul__a">
                {t(
                  'chat_room_detail_modal.body_section.setting.eternal_image.title'
                )}
              </span>

              <div className="ChatSettings__body__ps">
                <p
                  className="ChatSettings__body__p"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'chat_room_detail_modal.body_section.setting.eternal_image.body'
                    ),
                  }}
                />
              </div>
            </div>

            <Switch
              name="imageEternalModeCheckbox"
              defaultChecked={!isEternalImageMode} // If eternal image is true checkbox will off.
              onChange={(checked) => {
                dispatch(
                  setEternalImageModeTo({
                    chatRoomId: chatRoom?.id,
                    isActive: !checked,
                  })
                )
              }}
            />
          </div>
        ),
      },
    ]
  }

  return (
    <div className="ChatSettings__body">
      <h4 className="ChatSettings__body__h4">{t('common.setting')}</h4>

      <ul className="ChatSettings__body__ul">
        {getSettingList().map((setting, i) => (
          <li key={i}>
            {setting.to && (
              <Link className="ChatSettings__body__ul__a" to={setting.to}>
                {setting.label}
              </Link>
            )}

            {setting.onClick && (
              <a
                className={cx('ChatSettings__body__ul__a', setting.className)}
                onClick={setting.onClick}
              >
                {setting.label}
              </a>
            )}

            {setting.element ?? null}
          </li>
        ))}
      </ul>
    </div>
  )
}

const GroupChatReportConfirmBox = ({
  isActive,
  onClose,
  members,
  chatRoomId,
}) => {
  const { t } = useTranslation()
  const reportModal = useReportModal()

  return (
    <Modal
      className="GroupChatReportConfirmBox"
      onClose={onClose}
      isActive={isActive}
      title={t('common.report')}
    >
      <UserList
        users={members}
        actionButton={(user) => {
          return (
            <a
              onClick={() =>
                reportModal.open({
                  chatRoomId,
                  userId: user.id.toString(),
                })
              }
            >
              <Button buttonStyle="bordered-green">{t('common.report')}</Button>
            </a>
          )
        }}
      />
    </Modal>
  )
}

const ChatRoomDetailModal = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const { generatePageModalUrl } = usePageModal()

  const groupChatReportConfirmBox = useModalState()

  const { data: currentUser } = useCurrentUser()

  const chatRoomId = useMemo(() => {
    return queryString.parse(location.search)?.chatRoomId
  }, [location.search])

  const chatRoom = useGetChatRoomQuery({ chatId: Number(chatRoomId) })

  const isGroupChat = chatRoom?.data?.is_group

  const memberInChatRoomExceptSelf = (chatRoom?.data?.members ?? []).filter(
    (member: any) => member.id !== currentUser?.id
  )

  return (
    <Fragment>
      <div className="Modal__wrapper">
        <div className="Modal__header">
          <Link
            className="Modal__header__close Modal__header__close--back"
            to={generatePageModalUrl(MODAL_MODE.CHAT_ROOM, { chatRoomId })}
          />

          <h2 className="Modal__header__h2">
            {t('chat_room_detail_modal.title')}
          </h2>
        </div>

        <div className="Modal__body Modal__body--gray">
          <div className="ChatSettings">
            <HeaderSection
              isGroupChat={isGroupChat}
              members={memberInChatRoomExceptSelf}
            />

            <BodySection
              members={memberInChatRoomExceptSelf}
              chatRoom={chatRoom.data}
              isGroupChat={isGroupChat}
              showReportUsersModal={() => groupChatReportConfirmBox.open()}
            />
          </div>
        </div>
      </div>

      <GroupChatReportConfirmBox
        isActive={groupChatReportConfirmBox.isActive}
        onClose={groupChatReportConfirmBox.close}
        chatRoomId={chatRoomId}
        members={memberInChatRoomExceptSelf}
      />
    </Fragment>
  )
}

export default ChatRoomDetailModal
